import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import Separator from '../../../../components/Separator';
import { LicenseField } from '../LicenseField';

import { getFloatingNumberRegexp, INTEGER_REGEXP } from '../../../../constants/regex';
import { type IAdditionalFieldConfig } from '../../types/response';
import { FieldPrefix } from '../../constants/fieldPrefixes';
import { ArrayFields } from '../../types/fieldEnums';
import { DataType } from '../../types/enums';

import { getDateDecreasedByDays, getDateIncreasedByDays } from '../../../../helpers/dateHelpers';
import useDependsOnField from './useDependsOnField';

import styles from './AdditionalFields.module.scss';

interface IAdditionalFieldProps {
  config: IAdditionalFieldConfig;
  fieldNamePrefix?: string;
  configNamePrefix?: string;
  viewMode?: boolean;
}

function AdditionalField({ config, fieldNamePrefix = '', configNamePrefix = '', viewMode }: IAdditionalFieldProps) {
  const { i18n } = useTranslation();

  const fieldPrefix =
    config.dataType === DataType.OPTIONS_FROM_DATABASE
      ? FieldPrefix.dbOptionAdditionalFields
      : FieldPrefix.additionalFields;
  const additionalFieldName = `${fieldNamePrefix}${fieldPrefix}${FieldPrefix.additionalField}${config.id}`;
  const configName = `${configNamePrefix}${FieldPrefix.additionalFields}${FieldPrefix.additionalField}${config.id}`;

  const label = i18n.resolvedLanguage === 'en' ? config.englishLabel : config.nationalLanguageLabel;
  const placeholder = i18n.resolvedLanguage === 'en' ? config.englishPlaceHolder : config.nationalLanguagePlaceHolder;

  const maxFieldUiLength = fieldNamePrefix?.includes(ArrayFields.items) ? 2 : 3;

  const fieldUILengthClassName = config.fieldUILength
    ? `grid-columns-size-${config.fieldUILength <= maxFieldUiLength ? config.fieldUILength : maxFieldUiLength}`
    : '';

  const showField = useDependsOnField({ config, fieldNamePrefix, additionalFieldName });

  const fieldProps = {
    name: additionalFieldName,
    configName,
    label,
    placeholder,
    viewMode,
  };

  switch (config.dataType) {
    case DataType.CHECKBOX:
      return (
        <LicenseField.Checkbox
          {...fieldProps}
          className={cn({
            [styles.invisibleField as string]: !showField,
          })}
          alignment="top"
        />
      );
    case DataType.DECIMAL:
      return (
        <LicenseField.TextField
          {...fieldProps}
          className={cn({
            [styles.invisibleField as string]: !showField,
          })}
          regexp={getFloatingNumberRegexp(config.scale)}
          fractionDigits={config.scale}
          triggerOnChange
          formatAsNumber
        />
      );
    case DataType.INTEGER:
      return (
        <LicenseField.TextField
          {...fieldProps}
          className={cn({
            [styles.invisibleField as string]: !showField,
          })}
          regexp={INTEGER_REGEXP}
          triggerOnChange
        />
      );
    case DataType.TEXT:
      return (
        <LicenseField.TextField
          className={cn(fieldUILengthClassName, {
            [styles.invisibleField as string]: !showField,
          })}
          {...fieldProps}
          triggerOnChange
        />
      );
    case DataType.TEXTAREA:
      return (
        <LicenseField.TextField
          className={cn(fieldUILengthClassName, styles.unsetHeight, {
            [styles.invisibleField as string]: !showField,
          })}
          {...fieldProps}
          defaultValue={showField ? config.defaultValue ?? '' : ''}
          // @ts-expect-error
          rows={config.textAreaHeight ?? 4}
          triggerOnChange
          textarea
        />
      );
    case DataType.DATE:
      return (
        <LicenseField.DateField
          {...fieldProps}
          {...{ maxDate: getDateIncreasedByDays(config?.maxDate), minDate: getDateDecreasedByDays(config?.minDate) }}
          className={cn({
            [styles.invisibleField as string]: !showField,
          })}
        />
      );
    case DataType.LIST:
      return (
        <LicenseField.SelectField
          {...fieldProps}
          className={cn({
            [styles.invisibleField as string]: !showField,
          })}
          options={config?.listOfOptions?.map((value) => ({ label: value, value }))}
          isClearable
        />
      );
    case DataType.OPTIONS_FROM_DATABASE:
      return (
        <LicenseField.AdditionalFieldSelect
          {...fieldProps}
          sectionClassName={cn({
            [styles.invisibleField as string]: !showField,
          })}
          minSuggest={config.minNumberOfCharacters}
          maxFieldUILength={maxFieldUiLength}
          className={fieldUILengthClassName}
          config={config}
        />
      );
    case DataType.LABEL:
      return (
        <h3
          className={cn('grid-full-width', {
            [styles.invisibleField as string]: !showField,
          })}
        >
          {label}
        </h3>
      );
    case DataType.SEPARATOR:
      return <Separator className="grid-full-width" />;
    default:
      return null;
  }
}

export default AdditionalField;
